<template>
  <section class="map-view">
    <RoomModal
      v-if="isopenRoomModal"
      @closeModal="closeRoomModal"
      :roomProp="room"
      :surveys="surveys"
      :roomTypes="roomTypes"
      @submit="submitRoom"
    />
    <Modal
      v-if="isOpenBulkModal"
      width="large"
      :texts="bulkModalTexts"
      :showCloseCorner="!bulkSubmitting"
      @close="onBulkModalClose"
      @submit="submitBulkRooms"
      :isConfirmDisabled="!bulkHasAnyRooms || bulkSubmitting"
    >
      <div class="bulk-form-wrapper" :class="{ 'bulk-form-wrapper-loading': bulkSubmitting }">
        <div v-if="bulkSubmitting" class="bulk-overlay" aria-live="polite">
          <div class="bulk-overlay-content">
            <div class="bulk-spinner" />
            <p class="bulk-overlay-text">{{ $t('adding_rooms') || 'Adding rooms...' }}</p>
            <p class="bulk-overlay-count">{{ bulkTotalRoomCount }} {{ $t('rooms') }}</p>
          </div>
        </div>
        <section class="bulk-form">
          <p class="bulk-intro">{{ $t('bulk_rooms_intro') || 'Add one or more groups. Each group has its own building, floor, zone, gender and room names.' }}</p>
          <div
            v-for="(grp, idx) in bulkGroups"
            :key="grp.id"
            class="bulk-group"
          >
            <div class="bulk-group-header">
              <span class="bulk-group-title">{{ $t('group') || 'Group' }} {{ idx + 1 }}</span>
              <Button
                v-if="bulkGroups.length > 1"
                type="button"
                :isClean="true"
                class="bulk-group-remove"
                @click="removeBulkGroup(idx)"
              >{{ $t('remove') || 'Remove' }}</Button>
            </div>
            <div class="bulk-fields-row">
              <div class="bulk-field">
                <label>{{ $t('building') }}</label>
                <input v-model="grp.building" type="text" class="bulk-input" :placeholder="$t('building')" />
              </div>
              <div class="bulk-field">
                <label>{{ $t('floor') }}</label>
                <input v-model="grp.floor" type="text" class="bulk-input" :placeholder="$t('floor')" />
              </div>
              <div class="bulk-field">
                <label>{{ $t('zone') }}</label>
                <input v-model="grp.zone" type="text" class="bulk-input" :placeholder="$t('zone')" />
              </div>
              <div class="bulk-field">
                <label>{{ $t('gender') }}</label>
                <select v-model="grp.gender" @change="onBulkGenderChange(grp)" :class="['bulk-input', 'bulk-select', { 'bulk-select-error': bulkErrors.includes(grp.id) }]">
                  <option value="" disabled>{{ $t('please_select') }}</option>
                  <option v-for="g in roomTypes" :key="g" :value="g">{{ $roomTypeLabel(g) }}</option>
                </select>
                <label class="bulk-pad-option">
                  <input type="checkbox" v-model="grp.includingPad" @change="grp.includingPadTouched = true" />
                  <span>{{ $t('including_pad9') }}</span>
                </label>
              </div>
            </div>
            <p class="bulk-desc">{{ $t('room_names_one_per_line') || 'Room names (one per line)' }}</p>
            <textarea
              :ref="idx === 0 ? 'firstBulkTextarea' : undefined"
              v-model="grp.roomNamesText"
              class="bulk-textarea"
              rows="4"
              :placeholder="$t('room_names_placeholder') || 'Room 1\nRoom 2'"
              @paste="onBulkRoomNamesPaste($event, idx)"
            />
            <div class="bulk-generator">
              <input v-model.number="grp.generateCount" type="number" min="1" max="100" class="bulk-input small" />
              <span class="bulk-generator-label">{{ $t('rooms_with_prefix') || 'rooms with prefix' }}</span>
              <input v-model="grp.generatePrefix" type="text" class="bulk-input small" placeholder="Room" />
              <Button type="button" @click="applyBulkGeneratedForGroup(idx)">{{ $t('apply') || 'Apply' }}</Button>
            </div>
          </div>
          <Button type="button" :isClean="true" class="bulk-add-group-btn" @click="addBulkGroup">{{ $t('add_group') || '+ Add another group' }}</Button>
          <p v-if="bulkTotalRoomCount > 0 && !bulkSubmitting" class="bulk-summary">
            {{ $t('bulk_summary') || 'Summary' }}: {{ bulkTotalRoomCount }} {{ $t('rooms') }} {{ $t('in') }} {{ bulkGroups.length }} {{ $t('group') }}(s)
          </p>
        </section>
      </div>
    </Modal>
    <DeviceModal
      v-if="isOpenDeviceModal && (room.id || selectedDevice.deviceCode)"
      @close="closeDeviceModal"
      :selectedRoom="room"
      @submit="submitDevice"
      :selectedDevice="selectedDevice"
    />
    <transition name="fade">
      <div class="custom-button-container">
        <span>{{ $t("search") }}:</span>
        <input type="text" v-model="searchByName" />
        <Button
          @click="removeSelectedRooms"
          v-if="isAdmin"
          :disabled="!selectedRooms.length"
          skin="accent"
          >{{ $t("delete") }}</Button
        >
        <Button @click="openRoomModal" v-if="isAdmin" skin="primary">{{
          $t("new_room")
        }}</Button>
        <Button
          @click="openBulkModal"
          v-if="isAdmin"
          skin="primary"
          :disabled="!siteId"
          :title="!siteId ? $t('please_select_site_first') : ''"
        >{{ $t("bulk_add_rooms") }}</Button>
      </div>
    </transition>

    <Tabs class="tabs-container" :isRTL="isRTL" syncRoute>
      <Tab :label="$t('rooms')" to="/admin/room/list">
        <section class="map-details">
          <Loading v-if="isLoading" class="loader" />
          <Table
            v-else-if="roomsToShow.length > 0 && siteId"
            :tableData="roomsToShow"
            nestedIdField="id"
            @deleteNestedItem="removeSensor"
            :isNested="true"
            :isNestedEditable="false"
            :isSelect="isAdmin"
            idField="id"
            @select="handleSelectedItems"
            :editOptions="{ remove: isAdmin, edit: isAdmin }"
            :extraActions="[{ name: 'room-info', type: 'openRoomInfo' }, { name: 'qr_code', type: 'generateQR' }]"
            @action="handleActionClick"
            :isAdmin="isAdmin"
            @editRow="editRow"
            @removeRow="removeRow"
            :buttonText="isAdmin ? $t('add_device') : ''"
            @buttonClick="openDeviceModal"
            @nestedButtonClick="assignDevice"
            :nestedButtonText="isAdmin ? $t('assign_device') : ''"
            :initialOpenNested="initialOpenNested"
            @updatedNestedOpen="updateInitialOpenNested"
            :isPaginated="true"
            :itemsPerPage="itemsPerPage"
            :initialPage="initialPage"
            :isSeeAllProp="isSeeAll"
            @toggleSeeAll="isSeeAll = !isSeeAll"
            @pageChange="onTablePageChange"
          />
          <div v-else-if="!isLoading && roomsToShow.length === 0 && siteId">
            <h3>{{ $t("no_rooms_found") }}</h3>
          </div>
        </section>
      </Tab>
      <!-- <Tab :label="$t('mapping')" to="/admin/room/generate">
        <MapGenerator v-if="siteId" @generated="updatePageData" />
        <div v-else>
          <h3>{{ $t("please select a company & site") }}</h3>
        </div>
      </Tab> -->
    </Tabs>
  </section>
</template>

<script>
import roomService from "../services/roomService";
import surveyService from '../../surveys/services/surveyService'
import swalService from "@/modules/common/services/swalService";
import MapGenerator from "./MapGenerator";
import RoomModal from "../components/RoomModal";
import DeviceModal from "../components/DeviceModal";
import Modal from "@/modules/common/components/Modal";
import sensorService from "../services/sensorService";
import Table from "@/modules/common/components/customTable/Table";
import Tabs from "@/modules/common/components/Tabs/Tabs";
import Tab from "@/modules/common/components/Tabs/Tab";
import Button from "@/modules/common/components/Button";
import Loading from "@/modules/common/components/Loading";
import { mapGetters } from 'vuex';
import QRCode from 'qrcode'

export default {
  data() {
    const search = this.$route?.query?.search;
    const page = Number(this.$route?.query?.page);
    return {
      rooms: [],
      surveys: [],
      mapRoomSurveys: {},
      room: roomService.getDefaultRoom(),
      isLoading: false,
      selectedRooms: [],
      isopenRoomModal: false,
      isOpenDeviceModal: false,
      selectedDevice: {},
      initialOpenNested: [],
      searchByName: typeof search === 'string' ? search : '',
      initialPage: page > 0 ? page : 1,
      itemsPerPage: 10,
      isSeeAll: false,
      isOpenBulkModal: false,
      bulkGroups: [],
      bulkSubmitting: false,
      bulkErrors: [],
      roomTypes: [],
      restroomTypes: [],
    };
  },
  methods: {
    updateInitialOpenNested(openNestedTableIndexes) {
      this.initialOpenNested = openNestedTableIndexes;
    },
    onTablePageChange(page) {
      if (page === this.initialPage) return;
      this.setInitialPage(page);
      this.syncListStateToRoute();
    },
    syncListStateToRoute() {
      if (!['room', 'roomList'].includes(this.$route.name)) return Promise.resolve();

      const query = { ...this.$route.query };
      if (this.searchByName) query.search = this.searchByName;
      else delete query.search;
      if (this.initialPage > 1) query.page = String(this.initialPage);
      else delete query.page;

      const sameSearch = (query.search || '') === (this.$route.query.search || '');
      const samePage = String(query.page || '') === String(this.$route.query.page || '');
      if (sameSearch && samePage) return Promise.resolve();

      return this.$router.replace({
        name: this.$route.name === 'room' ? 'roomList' : this.$route.name,
        query,
      }).catch((err) => {
        if (err?.name !== 'NavigationDuplicated') throw err;
      });
    },
    handleActionClick({ type, idx }) {
      if (type === "openRoomInfo") this.openRoomInfo({ idx })
      else if (type === "generateQR") this.generateQR({ idx })
    },
    /**
     * @param {{
     * idx: number;
     * type: string;
     * }} param0
     * */
    async openRoomInfo({ idx }) {
      if (!this.isAdmin) return
      const { id } = this.roomsToShow[idx];
      await this.syncListStateToRoute();
      this.$router.push({ name: "roomDetails", params: { id } });
    },
    async generateQR({ idx, type = '' }) {
      const qrTypes = ['qr tag', 'qr task', 'qr validator']
      if (type && !qrTypes.includes(type)) return swalService.errorMsg('Invalid QR code type')
      const domain = window.location.origin
      const room = this.roomsToShow[idx]
      const { ref_id, zone, gender, floor, building, display_name: roomName } = room
      let qrDomain = ''
      let deviceCode = ''
      if (type && qrTypes.includes(type)) {
        // Given a specific type, generate the QR code for that type
        const QR = room.nestedTable.find(({ type: qrType }) => qrType === type)
        if (!QR) return swalService.errorMsg('QR code is not found in this room')
        qrDomain = QR.type === 'qr validator' ? '' : QR.type === 'qr task' ? 'task-app' : 'survey-app'
        deviceCode = QR.deviceCode
      } else if (!type) {
        // find which QR code to generate
        const qrs = []
        qrTypes.forEach(qrType => {
          const qr = room.nestedTable.find(({ type }) => type === qrType)
          if (qr) qrs.push(qr)
        })
        if (qrs.length === 0) return swalService.errorMsg('QR code is not found in this room')
        if (qrs.length === 1) {
          qrDomain = qrs[0].type === 'qr validator' ? '' : qrs[0].type === 'qr task' ? 'task-app' : 'survey-app'
          deviceCode = qrs[0].deviceCode
        }
        if (qrs.length > 1) {
          // multiple qr codes found, print all qr codes
          qrs.forEach(qr => {
            this.generateQR({ idx, type: qr.type })
          })
          return
        }
      }
      const qrCodeImage = new Image()
      if (deviceCode.includes('QR:')) {
        deviceCode = deviceCode.replace('QR:', '')
      }
      const url = `${domain}/${qrDomain ? qrDomain + '/' : ''}?QR:${deviceCode}`
      const text = `${roomName} ${ref_id || ''} ${zone || ''} ${gender || ''} ${floor ? `Floor ${floor}` : ''} ${building || ''}`
      const qrCodeImageData = await QRCode.toDataURL(url, { 
        width: 300,
        height: 300, 
        color: {
          dark: '#000000', // QR code color
          light: '#00000000' // Background color (fully transparent)
        }
      })
      qrCodeImage.onload = function () {
        const canvas = document.createElement('canvas')
        canvas.width = qrCodeImage.width
        canvas.height = qrCodeImage.height + 30
        const ctx = canvas.getContext('2d')
        ctx.drawImage(qrCodeImage, 0, 0)
        ctx.font = '12px Arial'; // Set the font for the text
        ctx.fillText(text, 75, qrCodeImage.height); // Draw the text below the QR code
        const a = document.createElement('a')
        a.download = `${ref_id}-${qrDomain || 'validator'}-QR.png`
        a.href = canvas.toDataURL('image/png')
        a.click()
      }
      qrCodeImage.src = qrCodeImageData
    },
    async removeSelectedRooms() {
      const result = await swalService.confirmMsg();

      if (!result.value) return;

      try {
        const roomIds = this.selectedRooms.map(a => a.id);
        await roomService.removeRoomMany(roomIds);
        swalService.deletedMsg();
        this.setInitialPage(Math.ceil((this.rooms.length - roomIds.length) / this.itemsPerPage))
        this.updatePageData();
      } catch (err) {
        swalService.errorMsg();
      }
    },
    closeDeviceModal() {
      this.closeRoomModal();
      this.isOpenDeviceModal = false;
      this.selectedDevice = {};
    },
    async removeSensor(deviceCode, roomId, currentPage) {
      const result = await swalService.confirmMsg();
      if (!result.value) return;

      try {
        if (!deviceCode) throw new Error("no device code");
        await sensorService.remove(deviceCode);
        this.setInitialPage(currentPage);
        await this.updatePageData();
        swalService.deletedMsg();
      } catch (err) {
        swalService.errorMsg();
        console.warn(err.message);
      }
    },
    openDeviceModal(index) {
      this.isOpenDeviceModal = true;
      this.room = this.roomsToShow[index]
    },
    assignDevice(nestedIndex, roomIndex) {
      this.selectedDevice = this.roomsToShow[roomIndex].nestedTable[
        nestedIndex
      ];
      this.room = this.roomsToShow[roomIndex];
      this.isOpenDeviceModal = true;
    },
    closeRoomModal() {
      this.isopenRoomModal = false;
      this.room = roomService.getDefaultRoom();
    },
    async openRoomModal() {
      if (!(await this.ensureRoomTypes())) return;
      this.isopenRoomModal = true;
    },
    async ensureRoomTypes() {
      if (this.roomTypes.length) return true;
      try {
        const { types, restroomTypes } = await roomService.getRoomTypes();
        if (!Array.isArray(types) || !types.length || !Array.isArray(restroomTypes)) {
          throw new Error('room/types returned an invalid list');
        }
        this.roomTypes = types;
        this.restroomTypes = restroomTypes;
        return true;
      } catch (err) {
        console.error(err);
        swalService.errorMsg();
        return false;
      }
    },
    onBulkGenderChange(grp) {
      if (grp.includingPadTouched) return;
      grp.includingPad = this.restroomTypes.includes(grp.gender);
    },
    _newBulkGroup() {
      return {
        id: 'g' + Date.now() + Math.random().toString(36).slice(2),
        building: '',
        floor: '',
        zone: '',
        gender: '',
        includingPad: false,
        includingPadTouched: false,
        roomNamesText: '',
        generateCount: 5,
        generatePrefix: 'Room',
      };
    },
    async openBulkModal() {
      if (!this.siteId) return;
      if (!(await this.ensureRoomTypes())) return;
      this.isOpenBulkModal = true;
      this.bulkGroups = [this._newBulkGroup()];
      this.$nextTick(() => {
        const el = this.$refs.firstBulkTextarea;
        if (el) (Array.isArray(el) ? el[0] : el).focus();
      });
    },
    onBulkModalClose() {
      if (this.bulkSubmitting) return;
      this.closeBulkModal();
    },
    closeBulkModal() {
      this.isOpenBulkModal = false;
      this.bulkGroups = [];
      this.bulkErrors = [];
      this.bulkSubmitting = false;
    },
    addBulkGroup() {
      this.bulkGroups.push(this._newBulkGroup());
    },
    removeBulkGroup(idx) {
      this.bulkGroups.splice(idx, 1);
    },
    onBulkRoomNamesPaste(ev, groupIdx) {
      const pasted = (ev.clipboardData || window.clipboardData).getData('text');
      if (!pasted || typeof pasted !== 'string') return;
      const lines = pasted.split(/[\r\n]+/).flatMap((line) => line.split(/[\t,;]/).map((s) => s.trim()).filter(Boolean));
      if (lines.length <= 1) return;
      ev.preventDefault();
      const grp = this.bulkGroups[groupIdx];
      if (!grp) return;
      const existing = (grp.roomNamesText || '').split('\n').map((s) => s.trim()).filter(Boolean);
      grp.roomNamesText = [...existing, ...lines].join('\n');
    },
    applyBulkGeneratedForGroup(idx) {
      const grp = this.bulkGroups[idx];
      if (!grp) return;
      const n = Math.max(1, Math.min(100, parseInt(grp.generateCount, 10) || 1));
      const prefix = (grp.generatePrefix || 'Room').trim() || 'Room';
      const lines = [];
      for (let i = 1; i <= n; i++) {
        lines.push(`${prefix} ${i}`);
      }
      grp.roomNamesText = grp.roomNamesText ? grp.roomNamesText + '\n' + lines.join('\n') : lines.join('\n');
    },
    async submitBulkRooms() {
      const allRooms = [];
      this.bulkErrors = [];
      for (const grp of this.bulkGroups) {
        const lines = (grp.roomNamesText || '')
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
        if (lines.length && !grp.gender) this.bulkErrors.push(grp.id);
        for (const display_name of lines) {
          allRooms.push({
            display_name,
            building: (grp.building || '').trim(),
            floor: (grp.floor || '').trim(),
            zone: (grp.zone || '').trim(),
            gender: grp.gender,
            including_pad: !!grp.includingPad,
          });
        }
      }
      if (this.bulkErrors.length) {
        swalService.errorMsg(this.$t('room_type_required'));
        return;
      }
      if (!allRooms.length) {
        swalService.errorMsg(this.$t('enter_at_least_one_room'));
        return;
      }
      if (!this.siteId) {
        swalService.errorMsg(this.$t('no_site_selected') || 'No site selected.');
        return;
      }
      this.bulkSubmitting = true;
      try {
        await roomService.submitRoomsBulk(this.siteId, allRooms);
        const successMsg = (this.$t('bulk_rooms_success') || '').replace('{n}', allRooms.length) || `${this.$t('Your work has been saved')} (${allRooms.length} ${this.$t('rooms')})`;
        swalService.savedMsg(successMsg);
        this.closeBulkModal();
        this.setInitialPage(1);
        this.updatePageData();
      } catch (err) {
        swalService.errorMsg(err.response?.data || err.message || this.$t('something_went_wrong'));
      } finally {
        this.bulkSubmitting = false;
      }
    },
    async submitDevice(device) {
      try {
        if (this.selectedDevice.type) {
          await sensorService.updateSensor(device);
        } else {
          await sensorService.addSensor(device);
        }
        swalService.savedMsg(this.$t('Your work has been saved'))
        this.isOpenDeviceModal = false;
      } catch (err) {
        swalService.errorMsg();
      }
      this.updatePageData();
      this.selectedDevice = {};
    },
    async submitRoom(room) {
      delete room.device;
      try {
        if (!room.site_id) {
          this.addSiteIdToRoom(room)
        }
        await roomService.submitRoom(room);
        swalService.savedMsg(this.$t('Your work has been saved'))
        this.closeRoomModal();
      } catch (err) {
        swalService.errorMsg();
      }
      if (room.id) {
        this.setInitialPage(this.initialPage)
      } else {
        this.setInitialPage(Math.ceil((this.rooms.length + 1) / this.itemsPerPage))
      }
      this.updatePageData();
    },
    async editRow(rowData, currentPage) {
      const { id } = rowData;
      const roomData = this.rooms.find(a => a.id === id);
      this.roomDeviceTypes = (roomData.nestedTable || []).map(d => d.type);
      this.room = { ...this.room, ...roomData };
      this.room.pad_survey_id = this.mapRoomSurveys[this.room.id + 'pad_survey_id']
      this.room.survey_id = this.mapRoomSurveys[this.room.id + 'survey_id']
      this.room.no_shift_survey_id = this.mapRoomSurveys[this.room.id + 'no_shift_survey_id']
      delete this.room.nestedTable;
      this.setInitialPage(currentPage);
      if (!(await this.ensureRoomTypes())) return;
      this.isopenRoomModal = true;
    },
    async removeRow(roomId, currentPage) {
      const result = await swalService.confirmMsg();

      if (!result.value) return;
      try {
        await roomService.removeRoom(roomId);
        swalService.deletedMsg();
      } catch (err) {
        swalService.errorMsg();
      }
      this.updatePageData();
      this.setInitialPage(currentPage);
    },
    async updatePageData() {
      this.mapRoomSurveys = {}
      this.isLoading = true;
      this.addSiteIdToRoom(this.room)
      const currFilter = this.$store.getters.filterSelected;
      const rooms = await roomService.getRooms(currFilter);
      if (!rooms) {
        await swalService.errorMsg()
        return
      }
      const roomsProcessed = roomService.getProcessedRooms(rooms, this.$t('last'));
      this.rooms = roomsProcessed;
      this.rooms = this.rooms.map(room => {
        this.mapRoomSurveys[room.id + 'pad_survey_id'] = room.pad_survey_id
        this.mapRoomSurveys[room.id + 'survey_id'] = room.survey_id
        this.mapRoomSurveys[room.id + 'no_shift_survey_id'] = room.no_shift_survey_id
        delete room.pad_survey_id
        delete room.survey_id
        delete room.no_shift_survey_id
        return room;
      })
      this.surveys = await surveyService.getSurveyList({ site_id: this.siteId })
      this.isLoading = false;
    },
    handleSelectedItems(rooms) {
      this.selectedRooms = rooms;
    },
    addSiteIdToRoom(room) {
      room.site_id = this.siteId
    },
    setInitialPage(page) {
      this.initialPage = page
    }
  },
  watch: {
    searchByName(newVal, oldVal) {
      if (oldVal !== undefined && newVal !== oldVal) {
        this.initialPage = 1;
      }
      this.syncListStateToRoute();
    },
    '$store.getters.filterSelected.site': {
      handler(newSite, oldSite) {
        this.initialOpenNested = [];
        if (oldSite !== undefined && newSite !== oldSite) {
          this.searchByName = '';
          this.initialPage = 1;
          this.syncListStateToRoute();
        }
        this.updatePageData();
      },
      immediate: true,
    }
  },
  computed: {
    ...mapGetters(['isRTL', 'loggedUser']),
    siteId() {
      return this.$store.getters.filterSelected?.site;
    },
    currentFilteringSite() {
      return this.$store.getters['filter/currentFilteringSite'] || this.$store.getters.currentFilteringSite || null;
    },
    isAdmin() {
      return this.loggedUser.type === 'admin'
    },
    bulkHasAnyRooms() {
      return this.bulkGroups.some((g) => (g.roomNamesText || '').trim().length > 0);
    },
    bulkTotalRoomCount() {
      let count = 0;
      for (const grp of this.bulkGroups) {
        const lines = (grp.roomNamesText || '').split('\n').map((s) => s.trim()).filter(Boolean);
        count += lines.length;
      }
      return count;
    },
    bulkModalTexts() {
      const siteName = this.currentFilteringSite?.name;
      return {
        headerPrimary: 'bulk_add_rooms',
        headerSecondary: siteName ? `(${this.$t('adding_to') || 'Adding to'}: ${siteName})` : '',
        footerConfirm: 'add_rooms',
        footerCancel: 'cancel',
      };
    },
    roomsToShow() {
      const str = this.searchByName;
      const roomsData = this.rooms.map(room => {
        if (room) {
          const { cells_num, sinks_num, urinals_num, toilet_paper_size, display_name_en, target_effectiveness, ...roomToShow } = room
          return {
            ...roomToShow,
            is_calculated: room.is_calculated ? 'yes' : 'no',
            device: room.device ? room.device : 'none',
          }
        }
      })
      if (!str) {
        return roomsData
      } else {
        return roomsData.filter(room => {
          return room.display_name.toLowerCase().includes(str.toLowerCase())
        }
        )
      }
    },
  },
  components: {
    Table,
    Button,
    Loading,
    Tabs,
    Tab,
    RoomModal,
    DeviceModal,
    Modal,
    MapGenerator
  }
};
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;
@use "@/styles/views/map.scss";

.map-view {
  position: relative;
}

.custom-button-container {
  height: 80px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  position: absolute;
  z-index: 3;
  right: 0;
  @include rtl {
    left: 0;
    right: unset;
  }
  span {
    @include modal-input-label;
    margin-bottom: 0px;
    font-size: 13px;
  }
  input {
    @include modal-input;
    margin-inline-start: 5px;
    margin-bottom: 0px;
    padding: 0 5px;
    width: 150px;
  }
  button {
    font-size: 12px;
    font-weight: bold;
  }
}

/* Wrapper: when loading, fixed height so overlay covers full visible area */
.bulk-form-wrapper {
  position: relative;
  min-height: 120px;
}
.bulk-form-wrapper-loading {
  height: 70vh;
  overflow: hidden;
}
.bulk-form-wrapper-loading .bulk-form {
  pointer-events: none;
  user-select: none;
}
.bulk-form {
  padding: 4px 0 16px;
}
/* Full-area loading overlay */
.bulk-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 70vh;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(4px);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}
.bulk-overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
}
.bulk-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid #e2e8f0;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: bulk-spin 0.7s linear infinite;
}
.bulk-overlay-text {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #1e293b;
}
.bulk-overlay-count {
  margin: 0;
  font-size: 0.95rem;
  color: #64748b;
}
@keyframes bulk-spin {
  to { transform: rotate(360deg); }
}
.bulk-summary {
  margin: 20px 0 0;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #0369a1;
}
.bulk-intro {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 20px;
  line-height: 1.5;
  padding: 0 2px;
}
.bulk-group {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.bulk-group:hover {
  border-color: #cbd5e1;
}
.bulk-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}
.bulk-group-title {
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
}
.bulk-group-remove {
  font-size: 0.85rem;
  color: #64748b;
  padding: 6px 10px;
  min-height: auto;
  border-radius: 6px;
}
.bulk-group-remove:hover {
  color: #dc2626;
  background: #fef2f2;
}
.bulk-add-group-btn {
  margin-bottom: 20px;
  width: 100%;
  min-height: 44px;
  padding: 14px 16px !important;
  line-height: 1.5;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.bulk-add-group-btn:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
  color: #334155;
}
.bulk-desc {
  margin: 0 0 8px;
  font-size: 0.875rem;
  color: #475569;
  font-weight: 500;
}
.bulk-textarea {
  width: 100%;
  min-height: 88px;
  padding: 12px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.bulk-textarea:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}
.bulk-generator {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.bulk-input {
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.bulk-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15);
}
.bulk-input.small {
  width: 72px;
}
.bulk-generator-label {
  font-size: 0.9rem;
  color: #64748b;
}
.bulk-fields-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 14px;
}
.bulk-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bulk-field label {
  font-size: 0.875rem;
  color: #334155;
  font-weight: 500;
}
.bulk-select {
  cursor: pointer;
  appearance: auto;
}
.bulk-select-error {
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.2);
}
.bulk-pad-option {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 0.85rem;
}
</style>